import { LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Breadcrumb, Button, Dropdown, Layout, Menu, Segmented, Space, Typography } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import { useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminMenu, pageLabels, type AdminMenuNode } from '../config/menu';
import { setContentLanguage, useContentLanguage, type ContentLanguage } from '../i18n/content-language';

const { Header, Sider, Content } = Layout;

export function AdminLayout() {
  const { user, logout, can } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const contentLanguage = useContentLanguage();
  const [collapsed, setCollapsed] = useState(false);

  const visibleMenu = useMemo(() => {
    const map = (nodes: AdminMenuNode[]): ItemType[] => nodes
      .map((node) => {
        const children = node.children ? map(node.children) : undefined;
        if (!can(node.permission) && !children?.length) return null;
        return { key: node.path ?? node.key, icon: node.icon, label: node.label, children } as ItemType;
      })
      .filter(Boolean) as ItemType[];
    return map(adminMenu);
  }, [can]);

  const allPaths = useMemo(() => adminMenu.flatMap((node) => node.children ?? [node]).map((node) => node.path).filter(Boolean) as string[], []);
  const selected = [...allPaths].sort((a, b) => b.length - a.length).find((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  const pathParts = location.pathname.split('/').filter(Boolean);
  const breadcrumbs = pathParts.map((part, index) => ({
    title: index === pathParts.length - 1 ? pageLabels[part] ?? (part === 'new' ? '新增' : '编辑') : <Link to={`/${pathParts.slice(0, index + 1).join('/')}`}>{pageLabels[part] ?? part}</Link>,
  }));
  const changeContentLanguage = (language: ContentLanguage) => {
    setContentLanguage(language);
    const detailRoute = location.pathname.match(/^\/(products|projects|solutions)\/[0-9a-f-]{36}$/i);
    if (detailRoute) navigate(`/${detailRoute[1]}`, { replace: true });
  };

  return (
    <Layout className="admin-shell">
      <Sider width={248} collapsed={collapsed} collapsible trigger={null} className="admin-sider">
        <div className="admin-brand"><div className="admin-brand__mark">S</div>{!collapsed && <div><strong>SHINEHIVE</strong><span>CONTENT ADMIN</span></div>}</div>
        <Menu theme="dark" mode="inline" items={visibleMenu} selectedKeys={selected ? [selected] : []} onClick={({ key }) => key.startsWith('/') && navigate(key)} />
      </Sider>
      <Layout>
        <Header className="admin-header">
          <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed(!collapsed)} />
          <Space size="middle">
            <Space size="small">
              <Typography.Text type="secondary">内容配置</Typography.Text>
              <Segmented
                aria-label="切换内容配置语言"
                value={contentLanguage}
                options={[
                  { value: 'en', label: '英文' },
                  { value: 'es', label: '西班牙语' },
                ]}
                onChange={(value) => changeContentLanguage(value as ContentLanguage)}
              />
            </Space>
            <Typography.Text type="secondary">{user?.systemRole === 'SUPER_ADMIN' ? '超级管理员' : user?.systemRole === 'ADMIN' ? '管理员' : '成员'}</Typography.Text>
            <Dropdown menu={{ items: [
              { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true, onClick: () => void logout().then(() => navigate('/login')) },
            ] }}>
              <Button type="text"><Space><Avatar size="small" icon={<UserOutlined />} />{user?.displayName || user?.username}</Space></Button>
            </Dropdown>
          </Space>
        </Header>
        <Content className="admin-content">
          <Breadcrumb items={[{ title: <Link to="/">管理后台</Link> }, ...breadcrumbs]} className="admin-breadcrumb" />
          <Outlet key={contentLanguage} />
        </Content>
      </Layout>
    </Layout>
  );
}
